/**
 * Map a resolved module + its `widget.*` props to a `ModuleCardInput` — the
 * pure version of the `arch-scene-module` widget's render logic, with no DOM.
 * Lets the server compose the same card the live board paints.
 */
import type { ModuleCardInput, ModuleChip } from "../card.js";
import type { Module } from "../arch-model/model.js";
import type { WidgetPropValue } from "../arch-model/widget-spec.js";
import type { Tokens } from "../tokens.js";
import { getFieldString, splitCasedName } from "../arch-model/utils.js";
import { paletteColor, type PaletteTheme } from "../palette.js";

export interface ModuleToCardOptions {
  /** Resolve an icon id to its inner-SVG body (against a 24x24 viewBox). */
  readonly resolveIcon?: (id: string) => string | undefined;
  readonly defaultAccent?: string;
  /** Force subspace mode (the board signals it via `data-has-subspace`);
   *  when omitted it's inferred from the module having children. */
  readonly subspace?: boolean;
  /** Accent that WINS over `widget.color` — the board's unified node colour
   *  (aspect / label / diff), which isn't a model field. */
  readonly accentOverride?: string;
  /** Build the palette for a given accent. Defaults to `defaultTokens` (the
   *  hardcoded headless palette). The LIVE board passes a resolver that reads
   *  the active `data-theme` CSS vars, so on-screen cards follow the theme while
   *  headless SVG/PNG export stays on the concrete defaults. */
  readonly makeTokens?: (accent?: string) => Tokens;
  /** Theme whose palette values colour names resolve through. */
  readonly theme?: PaletteTheme;
}

export function moduleToCardInput(
  module: Module,
  props: ReadonlyMap<string, WidgetPropValue>,
  opts: ModuleToCardOptions = {},
): ModuleCardInput {
  const p = (k: string): string | undefined => {
    const v = props.get(k);
    return v == null ? undefined : String(v);
  };
  const rawName = p("name") ?? module.name ?? "";
  const name = splitCasedName(rawName) || rawName;
  // Every text prop arrives already resolved — a getter (`widget.subheader:
  // @@team`) was evaluated in the engine, per node, after cascade. A colour
  // arrives as a palette NAME or a literal; `paletteColor` turns the former
  // into this theme's pixels and passes the latter through.
  const rawAccent =
    opts.accentOverride ?? p("color") ?? p("accent") ?? getFieldString(module, "accent") ?? opts.defaultAccent ?? "info";
  const accent = paletteColor(rawAccent, opts.theme);

  const subheader = p("subheader") ?? module.typeName ?? "";
  const footer = p("footer") ?? "";

  // A chip shows `key: value`; the key comes from the getter the prop was
  // resolved from, published by `extractWidgetSpec` as `<prop>Key`.
  const chips: ModuleChip[] = (["label1", "label2"] as const)
    .map((slot) => ({ key: p(`${slot}Key`), value: p(slot) ?? "" }))
    .filter((r) => r.value)
    .map((r) => ({ key: r.key || undefined, value: r.value }));

  // `extractWidgetSpec` keys props by the verbatim source segment, so
  // `widget.badgeColor` → `badgeColor` (kebab fallback for hand-built maps).
  const badgeColor = p("badgeColor") || p("badge-color");
  const iconId = p("icon") ?? "module";
  const anchors = (p("anchors") ?? "")
    .split(",").map((s) => s.trim())
    .filter((s): s is "in" | "out" | "top" | "bottom" => ["in", "out", "top", "bottom"].includes(s));

  return {
    name,
    unnamed: module.unnamed || undefined,
    accent,
    subheader: subheader || undefined,
    footer: footer || undefined,
    description: module.description || undefined,
    iconBody: opts.resolveIcon?.(iconId) || undefined,
    // A synthesized stub (v0.6, §4.13) always renders dashed; an explicit
    // `widget.dashed: true` also opts in.
    dashed: module.synthesized === true || p("dashed") === "true",
    bg: p("bg") ? paletteColor(p("bg")!, opts.theme) : undefined,
    chips: chips.length ? chips : undefined,
    // Widget badge wins; otherwise fall back to the module's space (v0.5) so
    // space-scoped nodes carry a visible tag.
    badge: p("badge") || module.space || undefined,
    badgeColor: badgeColor ? paletteColor(badgeColor, opts.theme) : undefined,
    shape: p("shape") === "ellipse" ? "ellipse" : undefined,
    anchors: anchors.length ? anchors : undefined,
    subspace: opts.subspace ?? (module.children?.length ?? 0) > 0,
  };
}
